import type { Finding } from './api.ts'
import type { GuidedStep, RailTone } from './guidedSteps.ts'
import { ruleTitle } from './activeRules.ts'
import { columnStatusFor, needsPerson } from './verificationItems.ts'
import { CHECK_LABEL, label } from './vocabulary.ts'

/**
 * The divergence-only pass (#641): one step per finding somebody has to
 * answer, cut from the full pass.
 *
 * The full pass walks every entry because it is the record that each one was
 * reviewed. This one is opt-in and makes no such claim: it is the shortest way
 * through what diverged, and the full pass stays one click away.
 *
 * **What counts** is what the review gate counts -- `divergence` and
 * `undecided`, operational rules that ask included. `info` (a não orçado, a
 * rule's reminder) and `ok` are left out: nothing has to be said about them,
 * so a step for them would be a step with nothing to do.
 *
 * **Answered ones stay.** A step that vanished the moment it was answered
 * would move the list under the click, change what "3 de 7" means mid-pass,
 * and leave no way back to an answer somebody wants to take back.
 *
 * **One step per finding**, even two on the same row of the same preparation: each
 * step answers its own. Built from the full pass's steps rather than from the
 * findings again, so the order and the grouping are the full pass's own.
 */
export function buildDivergenceSteps(steps: readonly GuidedStep[]): GuidedStep[] {
  const seen = new Set<string>()
  const cut: GuidedStep[] = []
  for (const step of steps) {
    for (const finding of step.findings) {
      if (!needsPerson(finding.disposition) || seen.has(finding.fingerprint)) continue
      // A finding can sit in more than one entry's view; it is one divergence.
      seen.add(finding.fingerprint)
      cut.push({
        id: `divergence:${finding.fingerprint}`,
        kind: step.kind,
        title: step.title,
        badge: divergenceField(finding),
        status: columnStatusFor(finding.disposition),
        findings: [finding],
        ...(step.item === undefined ? {} : { item: step.item }),
        regions: finding.regions,
        divergence: finding,
        parentId: step.id,
      })
    }
  }
  return cut
}

/** What the divergence is about, in the words the comparison's sections use. */
export function divergenceField(finding: Finding): string {
  if (finding.check === 'operational')
    return ruleTitle(finding) ?? label(CHECK_LABEL, 'operational')
  return label(CHECK_LABEL, finding.check)
}

/** The full pass's entries the divergence-only pass leaves out: nothing to answer on them. */
export function entriesWithoutDivergence(steps: readonly GuidedStep[]): number {
  return steps.filter((step) => !step.findings.some((f) => needsPerson(f.disposition))).length
}

/**
 * Where to stand after switching passes.
 *
 * Into the divergence-only pass, on the first divergence of the entry
 * somebody was on; back to the full pass, on the entry the divergence belongs
 * to. Anywhere else -- an entry with nothing to answer -- the start.
 */
export function placeAfterSwitch(
  steps: readonly GuidedStep[],
  from: GuidedStep | undefined,
): number {
  if (from === undefined) return 0
  const same = steps.findIndex((step) => step.id === from.id)
  if (same !== -1) return same
  const cutFrom = steps.findIndex((step) => step.parentId === from.id)
  if (cutFrom !== -1) return cutFrom
  const entry = steps.findIndex((step) => step.id === from.parentId)
  return entry === -1 ? 0 : entry
}

/**
 * Where an answer given from the keyboard hands on to: the next divergence
 * still open, after this one and then from the start; nothing when none is.
 *
 * The full pass hands an answered row's place to the next row still open on
 * the same step. A step here is one divergence, so the next row to answer is
 * on the next step that still has one -- and pressing A, A, X down the pass
 * should not need an Enter between each to get there.
 */
export function nextOpenDivergence(
  steps: readonly GuidedStep[],
  from: number,
  answered: ReadonlySet<string>,
): number | undefined {
  const open = (at: number) => {
    const divergence = steps[at]?.divergence
    return divergence !== undefined && !answered.has(divergence.fingerprint)
  }
  for (let at = from + 1; at < steps.length; at += 1) if (open(at)) return at
  for (let at = 0; at < from; at += 1) if (open(at)) return at
  return undefined
}

/**
 * A dot of the divergence-only rail: red until answered.
 *
 * Unlike the full pass's, whose dots say what each entry concluded, every
 * step here concluded the same thing. What is worth reading off the rail is
 * which of them are still somebody's to do.
 */
export function divergenceRailTone(step: GuidedStep, answered: ReadonlySet<string>): RailTone {
  return step.divergence !== undefined && answered.has(step.divergence.fingerprint)
    ? 'clean'
    : 'error'
}

/** "Divergência 3 de 7 · 2 respondidas · 5 em aberto": the rail counts divergences. */
export function divergenceCounterText(
  steps: readonly GuidedStep[],
  index: number,
  answered: ReadonlySet<string>,
): string {
  const total = steps.length
  if (total === 0) return 'Nenhuma divergência'
  const done = steps.filter((step) => divergenceRailTone(step, answered) === 'clean').length
  const parts = [`Divergência ${Math.min(index, total - 1) + 1} de ${total}`]
  if (done > 0) parts.push(done === 1 ? '1 respondida' : `${done} respondidas`)
  const open = total - done
  if (open > 0) parts.push(`${open} em aberto`)
  return parts.join(' · ')
}
